import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  combineLatestWith,
  firstValueFrom,
  forkJoin,
  interval,
  lastValueFrom,
  map,
  of,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { CaseTypeEnum } from '../../common/constants/temporary-constant';
import {
  CaseActivityLogsDtoListResponse,
  CaseDamageDetailDto,
  CaseDamageDetails,
  ClaimReferenceDocumentDto,
  ContainerMoveDto,
  CreateInvoice,
  CurrencyAndExchangeRateDto,
  CustomerInfoDto,
  CustomerRecoveryCaseDto,
  FormValidation,
  gcssBookingInfo,
  Inspection,
  InspectionDetailsDTO,
  InvoicingStatus,
  InvoiceDetail,
  LiabilityLetter,
  LiabilityLetterStatus,
  LiabilityParty,
  RecoveryCaseType,
  RecoveryRequest,
  SaveLiabilityLetter,
  SendGridEmail,
  SendMailRequest,
  UpdateInvoice,
  UpdateLiabilityLetterStatusVendor,
  UploadEmailRequest,
  VendorRecoveryCase,
  WorkOrderDto,
  WorkOrderLineItemDetail,
  WorkOrderLineItemDto,
} from '../../common/api/models';
import { ISharedRecoveryCaseService } from '../../shared-recovery-case-service';
import { TemplateModel } from '@maersk-global/angular-shared-library';
import {
  BlobService,
  CaseDamageDetailsService,
  InvoiceDetailService,
  LiabilityLetterService,
  LiabilityPartyService,
  NotificationService,
  RecoveryCaseDocumentService,
  RecoveryCaseService,
  RecoveryInspectionService,
} from '../../common/api';
import { SharedDataService } from '../../shared-data-service';
import { DcrpAuthorizationService } from '../../common/services/authorization/dcrp-authorization.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SharedVendorRecoveryCaseService
  implements ISharedRecoveryCaseService
{
  private apiVersion: string = '1.0';
  caseType: CaseTypeEnum | undefined;

  constructor(
    private _route: ActivatedRoute,
    private _recoveryClaimService: RecoveryCaseService,
    private _damageService: CaseDamageDetailsService,
    private _sharedDataService: SharedDataService,
    private _dcrpAuthorizationService: DcrpAuthorizationService,
    private _recoveryCaseDocumentService: RecoveryCaseDocumentService,
    private _recoveryInspectionService: RecoveryInspectionService,
    private _liabilityLetterService: LiabilityLetterService,
    private _liabilityPartyService: LiabilityPartyService,
    private _notificationService: NotificationService,
    private _invoiceDetailService: InvoiceDetailService,
    private _liabilityService: LiabilityLetterService,
    private _blobService: BlobService
  ) {}

  private shouldShowLiabilityDetailsSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private tabIndex$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private reloadDamageDetails$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private workOrderDateChange$$: BehaviorSubject<Date | null | undefined> =
    new BehaviorSubject<Date | null | undefined>(undefined);

  private currentStageId$$: BehaviorSubject<number> =
    new BehaviorSubject<number>(1);

  private reloadVendorRecoveryData$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private eirImageLastCheckedOn$$: BehaviorSubject<Date | undefined> =
    new BehaviorSubject<Date | undefined>(undefined);

  private formValidity$$: BehaviorSubject<FormValidation | null> =
    new BehaviorSubject<FormValidation | null>(null);

  private containerDetails$$: BehaviorSubject<
    ContainerMoveDto | null | undefined
  > = new BehaviorSubject<ContainerMoveDto | null | undefined>(undefined);

  private recoverableWorkOrderSelectedLineItems$$: BehaviorSubject<
    WorkOrderLineItemDetail[]
  > = new BehaviorSubject<WorkOrderLineItemDetail[]>([]);

  private selectedWorkOrder$$: BehaviorSubject<WorkOrderDto | null> =
    new BehaviorSubject<WorkOrderDto | null>(null);

  private workOrderLineItems$$: BehaviorSubject<WorkOrderLineItemDto[]> =
    new BehaviorSubject<WorkOrderLineItemDto[]>([]);

  private overviewDetails$$: BehaviorSubject<TemplateModel[]> =
    new BehaviorSubject<TemplateModel[]>([]);

  private reloadLiabilityLetters$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private reloadLiabilityPartyDetails$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private workFlowStageId$$: BehaviorSubject<number> =
    new BehaviorSubject<number>(1);

  workFlowStageId$: Observable<number> = this.workFlowStageId$$.asObservable();

  private reloadInvoices$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private shouldShowIssueInvoiceScreenSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private customerDataSubject$$: BehaviorSubject<CustomerInfoDto> =
    new BehaviorSubject<CustomerInfoDto>({});

  private caseCurrencyAndExchangeRate$$: BehaviorSubject<
    CurrencyAndExchangeRateDto | undefined
  > = new BehaviorSubject<CurrencyAndExchangeRateDto | undefined>(undefined);

  caseCurrencyAndExchangeRate$: Observable<
    CurrencyAndExchangeRateDto | undefined
  > = this.caseCurrencyAndExchangeRate$$.asObservable();

  vendorRecoveryCustomerData$: Observable<CustomerInfoDto> =
    this.customerDataSubject$$.asObservable().pipe(shareReplay(1));

  vendorRecoveryCaseData$: Observable<VendorRecoveryCase | undefined> =
    combineLatest([
      this._route.queryParamMap,
      this.reloadVendorRecoveryData$$.asObservable(),
    ]).pipe(
      switchMap(([params, reload]) => {
        const caseType = params.get('type');
        if (caseType != 'vendor') return of(undefined);
        const caseId = params.get('caseId');
        if (!caseId) return of(undefined);
        const request = {
          caseId: parseInt(caseId ?? ''),
          equipmentNumber: params.get('containerNumber') as string,
          recoveryCaseType: 'Vendor',
        } as RecoveryRequest;
        return this._recoveryClaimService.recoveryCasesGet(request).pipe(
          map((response) => {
            const data = response.data?.at(0);
            if (!reload || data?.recoveryStatusId == 1) {
              const currentStageId =
                !data || !data.workflowStageId ? 1 : data.workflowStageId;
              this.updateCurrentStageId(currentStageId);
            }
            return response.data?.at(0);
          })
        );
      }),
      tap((recoveryData) => {
        if (!recoveryData) return;
        this.workFlowStageId$$.next(recoveryData?.workflowStageId ?? 1);
        const caseCurrencyAndExchangeRate = {
          quotationCurrency: recoveryData.caseCurrency,
          exchangeRatePerUnit: recoveryData.exchangeRateUSDCaseCurrency,
        } as CurrencyAndExchangeRateDto;
        this.caseCurrencyAndExchangeRate$$.next(caseCurrencyAndExchangeRate);
      }),
      shareReplay(1)
    );

  currentStageId$: Observable<number> = this.currentStageId$$.asObservable();

  workOrderDate$: Observable<Date | null | undefined> =
    this.workOrderDateChange$$.asObservable();

  vendorRecoveryEirImageLastFetchDate$: Observable<Date | undefined> =
    this.eirImageLastCheckedOn$$.asObservable();

  vendorRecoveryContainerMovesDetails$: Observable<
    ContainerMoveDto | null | undefined
  > = this.containerDetails$$.asObservable();

  currentVendorRecoveryTabIndex$: Observable<number | 0> =
    this.tabIndex$$.asObservable();

  vendorRecoverableWorkOrderLineItem$: Observable<WorkOrderLineItemDetail[]> =
    this.recoverableWorkOrderSelectedLineItems$$.asObservable();

  selectedWorkOrder$: Observable<WorkOrderDto | null> =
    this.selectedWorkOrder$$.asObservable();

  vendorWorkOrderLineItems$: Observable<WorkOrderLineItemDto[]> =
    this.workOrderLineItems$$.asObservable();

  overviewDetails$: Observable<TemplateModel[]> =
    this.overviewDetails$$.asObservable();

  vendorRecoveryLiabilityLetters$: Observable<
    Array<LiabilityLetter> | undefined
  > = this.reloadLiabilityLetters$$.asObservable().pipe(
    combineLatestWith(this.vendorRecoveryCaseData$),
    switchMap(([, vendorRecovery]) => {
      if (!vendorRecovery || !vendorRecovery.id) return of(undefined);
      return this._liabilityLetterService
        .liabilityLetterGetAllLiabilityLettersRecoveryCaseIdGet(
          vendorRecovery.id,
          this.apiVersion
        )
        .pipe(map((response) => response));
    }),
    shareReplay(1)
  );

  vendorRecoveryInvoices$: Observable<Array<InvoiceDetail> | undefined> =
    this.reloadInvoices$$.asObservable().pipe(
      combineLatestWith(this.vendorRecoveryCaseData$),
      switchMap(([, vendorRecoveryCase]) => {
        if (!vendorRecoveryCase || !vendorRecoveryCase.id) return of(undefined);
        return this._invoiceDetailService
          .invoiceDetailsSearchGet(this.apiVersion, vendorRecoveryCase.id)
          .pipe(
            map((response) => response.data),
            catchError(() => of(undefined))
          );
      }),
      shareReplay(1)
    );

  shouldShowVendorRecoveryIssueInvoiceScreen$: Observable<boolean> =
    combineLatest([
      this.shouldShowIssueInvoiceScreenSubject$$.asObservable(),
      this.vendorRecoveryInvoices$,
      this._sharedDataService.countries$,
      this.vendorRecoveryCaseData$,
    ]).pipe(
      map(
        ([
          shouldShowIssueInvoiceScreen,
          invoices,
          countries,
          customerRecoveryCase,
        ]) =>
          (shouldShowIssueInvoiceScreen ||
            !invoices ||
            invoices.length === 0) &&
          !!countries.find(
            (country) =>
              country.code === customerRecoveryCase?.responsibleCountryCode &&
              country.isS4HanaMigrated
          )
      ),
      shareReplay(1)
    );

  private invoiceCreationPermissionInitial$: Observable<InvoicingStatus> =
    combineLatest([
      this.vendorRecoveryCaseData$,
      this.vendorRecoveryLiabilityLetters$,
    ]).pipe(
      switchMap(([vendorRecoveryCase, letters]) => {
        if (
          !letters ||
          letters.length === 0 ||
          !vendorRecoveryCase?.id ||
          !letters.find(
            (letter) =>
              letter.liabilityLetterStatus === 'ACCEPTED' ||
              letter.liabilityLetterStatus === 'NO_RESPONSE'
          )
        )
          return of({
            totalSecondsRemainingToInvoice: 0,
            isReadyToInvoice: false,
          });
        // TODO: hardcoded the time duration for ready to invoice for now.
        // We have to check whether to call API for this same as customer recovery or not.
        return of({
          totalSecondsRemainingToInvoice: 0,
          isReadyToInvoice: true,
        });
      }),
      shareReplay(1)
    );

  vendorRecoveryInvoiceCreationPermission$: Observable<
    InvoicingStatus & { displayMessage: string }
  > = combineLatest([
    this.invoiceCreationPermissionInitial$,
    this.invoiceCreationPermissionInitial$.pipe(
      switchMap((caseInvoiceStatus) => {
        return interval(1000).pipe(
          take((caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1) + 1)
        );
      })
    ),
  ]).pipe(
    map(([caseInvoiceStatus, seconds]) => {
      const displayMessage =
        this._sharedDataService.convertSecondsToDayHourMinSec(
          (caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1) - seconds
        );
      if (
        !caseInvoiceStatus.isReadyToInvoice &&
        seconds < (caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1)
      )
        return {
          totalSecondsRemainingToInvoice:
            caseInvoiceStatus.totalSecondsRemainingToInvoice,
          isReadyToInvoice: caseInvoiceStatus.isReadyToInvoice,
          displayMessage: displayMessage,
        };
      else
        return {
          totalSecondsRemainingToInvoice: 0,
          isReadyToInvoice:
            caseInvoiceStatus.isReadyToInvoice ||
            caseInvoiceStatus.totalSecondsRemainingToInvoice !== 0,
          displayMessage: displayMessage,
        };
    })
  );

  enableCloseBtn$: Observable<boolean> = combineLatest([
    this.vendorRecoveryCaseData$,
    this.currentStageId$,
    this.vendorRecoveryInvoices$,
  ]).pipe(
    map(
      ([recoveryData, stageId, invoices]) =>
        !(
          // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
          (
            recoveryData?.caseStatus == 6 ||
            recoveryData?.caseStatus == 7 ||
            stageId == 4 ||
            (invoices && invoices.length > 0)
          )
        )
    )
  );

  reopenCase$: Observable<boolean> = this.vendorRecoveryCaseData$.pipe(
    map((recoveryData) => {
      // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
      return recoveryData?.caseStatus == 6 || recoveryData?.caseStatus == 7;
    })
  );

  disableForm$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.vendorRecoveryLiabilityLetters$,
    this.vendorRecoveryCaseData$,
    this.currentStageId$,
  ]).pipe(
    map(([isAuthorizedForRecoveryUpdate, letters, recoveryData, stageId]) => {
      if (
        !isAuthorizedForRecoveryUpdate ||
        // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
        recoveryData?.caseStatus == 6 ||
        recoveryData?.caseStatus == 7
      )
        return true;
      if (stageId == 1 && letters && letters.length > 0) {
        letters?.sort(
          (letter1, letter2) =>
            new Date(letter2.createdTimestamp ?? '').getTime() -
            new Date(letter1.createdTimestamp ?? '').getTime()
        );

        return letters.some(
          (letter) =>
            letter.liabilityLetterStatus === LiabilityLetterStatus.ACCEPTED
        );
      }
      return false;
    })
  );

  /**
   * We are calculating the accumulated state of the form validity. For e.g. If out of 2 forms 1 is valid and 1 is invalid,
   * the accumulated state would be invalid.
   */
  private collectiveFormValidation$ = this.formValidity$$.asObservable().pipe(
    withLatestFrom(this.currentStageId$, this.vendorRecoveryCaseData$),
    map(([formValidation, currentStageId, vendorRecoveryData]) =>
      formValidation
        ? {
            ...formValidation,
            stage: currentStageId,
            caseId: vendorRecoveryData?.id,
          }
        : null
    ),
    scan((formValidations, currentState) => {
      if (!currentState) return [];
      const matchingState = formValidations.find(
        (state) =>
          state.component === currentState.component &&
          state.stage === currentState.stage &&
          state.caseId === currentState.caseId
      );
      if (matchingState) matchingState.state = currentState.state;
      else formValidations.push(currentState);
      return formValidations;
    }, [] as FormValidation[])
  );

  private formValidityForCurrentStage$ = combineLatest([
    this.currentStageId$,
    this.collectiveFormValidation$,
    this.vendorRecoveryCaseData$,
  ]).pipe(
    map(([currentStageId, formValidations, vendorRecoveryData]) =>
      formValidations
        .filter(
          (validations) =>
            validations.stage === currentStageId &&
            validations.caseId === vendorRecoveryData?.id
        )
        .every((validation) => validation.state)
    )
  );

  enableNextBtn$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.vendorRecoveryCaseData$,
    this.currentStageId$,
    this.formValidityForCurrentStage$,
  ]).pipe(
    map(
      ([isAuthorizedForRecoveryUpdate, recoveryData, stageId, formValid]) =>
        formValid &&
        isAuthorizedForRecoveryUpdate &&
        // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
        recoveryData?.caseStatus != 6 &&
        recoveryData?.caseStatus != 7 &&
        stageId != 4
    )
  );

  hideNextBtn$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.currentStageId$,
    this.shouldShowVendorRecoveryIssueInvoiceScreen$,
  ]).pipe(
    map(([isAuthorizedForRecoveryUpdate, stageId, showInvoiceScreen]) => {
      let hiddenFlag: boolean = false;
      if (
        (stageId === 3 && !showInvoiceScreen) ||
        !isAuthorizedForRecoveryUpdate
      )
        hiddenFlag = true;
      return hiddenFlag;
    })
  );

  VendorRecoveryBookingCargoDetails$: Observable<gcssBookingInfo | undefined> =
    this.vendorRecoveryCaseData$.pipe(
      map((response) => {
        if (!response || !response.id) return undefined;
        return {
          invoiceNo: response.bolNumber,
          bookingNumber: response.bookingNumber,
        } as gcssBookingInfo;
      })
    );

  async saveDamageDetails(
    damages: TemplateModel[],
    caseId: number,
    caseCurrency: string
  ): Promise<boolean> {
    const damageDetailRequest: CaseDamageDetails[] = [];
    const currentItems = damages;

    currentItems?.forEach((damage: TemplateModel) => {
      if (
        !damage.hidden &&
        damage.items &&
        this._sharedDataService.validateDamageLineItem(damage.items)
      )
        damageDetailRequest.push({
          damageDescription: damage.items?.filter(
            (i) => i.name == 'description'
          )[0].value,
          damageAmountInUsd: damage.items?.filter((i) => i.name == 'amount')[0]
            .value,
          materialCodeId: damage.items?.filter(
            (i) => i.name == 'materialCode'
          )[0].value,
          id: damage.items?.filter((i) => i.name == 'id')[0].value,
          caseId: caseId,
          damageAmountInCaseCurrency: damage.items?.filter(
            (i) => i.name == 'amountInCaseCurrency'
          )[0].value,
          caseCurrency: caseCurrency,
        });
    });

    const saveSuccess = await lastValueFrom(
      this._damageService
        .caseDamageDetailsDeleteAndInsertPut(damageDetailRequest)
        .pipe(map((response) => response.isSuccess ?? false))
    );
    if (saveSuccess) this.reloadDamageDetails();
    return saveSuccess;
  }

  onResendEmailClicked(mail: SendMailRequest): Observable<boolean> {
    if (!mail || !mail.liabilityLetter) return of(false);
    const emailSendGridSendRequest = {
      to: mail.to,
      cc: mail.cc,
      from: mail.from,
      body: mail.body,
      subject: mail.subject,
      emailAttachments: mail.emailAttachment,
    } as SendGridEmail;

    const emailAPISaveRequest = {
      recoveryCaseId: mail.liabilityLetter.recoveryCaseId,
      liablePartyName: mail.liabilityLetter.liabilityPartyName,
      liabilityAmount: mail.liabilityLetter.liabilityAmount,
      liabilityLetterCaseType: 'Vendor',
      liabilityLetterResponseReason: '',
      emailBlobPath: '',
      to: emailSendGridSendRequest.to,
      cc: emailSendGridSendRequest.cc,
      subject: emailSendGridSendRequest.subject,
      body: emailSendGridSendRequest.body,
      emailAttachments: emailSendGridSendRequest.emailAttachments,
    } as SaveLiabilityLetter;

    const mailUploadReq = {
      caseId: mail.liabilityLetter.recoveryCaseId,
      emailType: 'Vendor_Notification_Email',
      fileName: '',
      recoveryCaseType: 'Vendor',
      from: emailSendGridSendRequest.from,
      to: emailSendGridSendRequest.to,
      cc: emailSendGridSendRequest.cc,
      subject: emailSendGridSendRequest.subject,
      body: emailSendGridSendRequest.body,
      emailAttachments: emailSendGridSendRequest.emailAttachments,
    } as UploadEmailRequest;

    return this._notificationService
      .notificationUploadEmailPost(mailUploadReq)
      .pipe(
        switchMap((mailUploadURI) =>
          forkJoin({
            mailSend: this._notificationService
              .notificationSendEmailPost(
                emailSendGridSendRequest,
                this.apiVersion
              )
              .pipe(map((response) => response as number)),
            mailSave: this._liabilityService.liabilityLetterPost(
              { ...emailAPISaveRequest, emailBlobPath: mailUploadURI },
              this.apiVersion
            ),
          })
        ),
        map(() => true),
        tap(() => this.reloadLiabilityLetters()),
        catchError(() => of(false))
      );
  }

  async updateLiabilityLetter(
    selectedLetter: LiabilityLetter,
    liabilityLetterStatus: LiabilityLetterStatus
  ): Promise<boolean> {
    const recoveryDataCaseID =
      (await firstValueFrom(this.vendorRecoveryCaseData$))?.id ?? 0;
    const request = {
      liabilityLetterStatus: liabilityLetterStatus,
      liabilityLetterResponseReason:
        selectedLetter.liabilityLetterResponseReason,
      recoveryCaseId: recoveryDataCaseID,
    } as UpdateLiabilityLetterStatusVendor;

    await lastValueFrom(
      this._liabilityLetterService
        .liabilityLetterIdPut(request, selectedLetter.id ?? 0)
        .pipe(
          catchError(() => {
            return of(false);
          })
        )
    );
    return true;
  }

  downloadLetter(emailBlobPath: string, caseNumber: string): Observable<Blob> {
    return this._blobService.blobDownloadGet(emailBlobPath);
  }

  damageDetails$: Observable<CaseDamageDetailDto[] | undefined> = combineLatest(
    [this.vendorRecoveryCaseData$, this.reloadDamageDetails$$.asObservable()]
  ).pipe(
    switchMap(([vendorRecoveryData]) => {
      if (!vendorRecoveryData || !vendorRecoveryData.id) return of(undefined);
      return this._damageService
        .caseDamageDetailsByCaseIdCaseIdGet(vendorRecoveryData?.id ?? 0)
        .pipe(
          map((response) => {
            if (!response || !response.data) return [];
            return response.data.map(
              (damage) =>
                ({
                  description: damage.damageDescription,
                  id: damage.id,
                  materialCode: damage.materialCodeId,
                  amount: damage.damageAmountInUsd,
                  amountInCaseCurrency: damage.damageAmountInCaseCurrency,
                  caseId: damage.caseId,
                  caseCurrency: damage.caseCurrency,
                }) as CaseDamageDetailDto
            );
          })
        );
    }),
    shareReplay(1)
  );

  shouldShowVendorRecoveryLiabilityDetails$: Observable<boolean> =
    combineLatest([
      this.shouldShowLiabilityDetailsSubject$$.asObservable(),
      this.vendorRecoveryLiabilityLetters$,
    ]).pipe(
      map(
        ([shouldShowLiabilityDetails, liabilityLetters]) =>
          shouldShowLiabilityDetails ||
          !liabilityLetters ||
          liabilityLetters.length === 0
      ),
      shareReplay(1)
    );

  vendorRecoveryLiablePartyData$: Observable<LiabilityParty | undefined> =
    combineLatest([
      this.vendorRecoveryCaseData$,
      this.reloadLiabilityPartyDetails$$.asObservable(),
    ]).pipe(
      switchMap(([vendorRecoveryData]) => {
        if (!vendorRecoveryData?.id) return of(undefined);
        return this._liabilityPartyService
          .liabilityPartiesGet(vendorRecoveryData.id ?? 0, this.apiVersion)
          .pipe(
            map((response) => {
              if (!response || !response.data) return;
              return response?.data as LiabilityParty;
            }),
            catchError((_) => {
              return of(undefined);
            })
          );
      })
    );

  resetCaseContext() {
    this.currentStageId$$.next(1);
    this.workFlowStageId$$.next(1);
    this.reloadVendorRecoveryData$$.next(false);
    this.updateWorkOrderLineItems([]);
    this.updateRecoverableWorkOrderSelectedLineItems([]);
    this.updateContainerDetails(undefined);
    this.updateWorkOrderDate(undefined);
  }

  updateFormValidationState(state: FormValidation) {
    this.formValidity$$.next(state);
  }

  reloadDamageDetails(): void {
    this.reloadDamageDetails$$.next(true);
  }

  updateLiabilityDetailsVisibility(value: boolean) {
    this.shouldShowLiabilityDetailsSubject$$.next(value);
  }

  updateEirImageLastFetchOn(date: Date | undefined) {
    this.eirImageLastCheckedOn$$.next(date);
  }

  reloadRecoveryCaseData(): void {
    this.reloadVendorRecoveryData$$.next(true);
  }

  resetRecoveryCaseData(): void {
    this.reloadVendorRecoveryData$$.next(false);
  }

  updateCurrentStageId(stageId: number): void {
    this.currentStageId$$.next(stageId);
  }

  updateTabSelected(tabIndex: number) {
    this.tabIndex$$.next(tabIndex);
  }

  updateContainerDetails(
    containerDetails: ContainerMoveDto | null | undefined
  ) {
    this.containerDetails$$.next(containerDetails);
  }

  updateRecoverableWorkOrderSelectedLineItems(
    lineItems: WorkOrderLineItemDetail[]
  ) {
    this.recoverableWorkOrderSelectedLineItems$$.next(lineItems);
  }

  updatedSelectedWorkOrder(workOrder: WorkOrderDto) {
    this.selectedWorkOrder$$.next(workOrder);
  }

  updateWorkOrderLineItems(lineItems: WorkOrderLineItemDto[]) {
    this.workOrderLineItems$$.next(lineItems);
  }

  updateCaseType(vendorRecoveryCase: CustomerRecoveryCaseDto) {
    if (vendorRecoveryCase.createdBy) {
      this.caseType =
        !vendorRecoveryCase.workOrderNumber &&
        !['sys', 'system'].includes(
          vendorRecoveryCase.createdBy.toLocaleLowerCase()
        )
          ? CaseTypeEnum.CaseWithOutWorkOrder
          : CaseTypeEnum.CaseWithWorkOrder;
    } else {
      this.caseType = CaseTypeEnum.CaseWithWorkOrder;
    }
  }

  // TODO: Temporary fix will be removed with case versioning changes.
  getCaseStatusByStageID(stageID: number): number {
    let caseStatus = 1; // New
    switch (stageID) {
      case 1:
        caseStatus = 1; // New
        break;
      case 2:
        caseStatus = 3; // Liability
        break;
      case 3:
        caseStatus = 4; // Invoice
        break;
      case 4:
        caseStatus = 5; // Complete
        break;
    }
    return caseStatus;
  }

  updateWorkOrderDate(date: Date | null | undefined) {
    this.workOrderDateChange$$.next(date);
  }

  caseDocumentGet(
    caseId: number
  ): Observable<ClaimReferenceDocumentDto | null> {
    return this._recoveryClaimService
      .recoveryCasesCaseIdAttachmentsGet(caseId, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  caseDocumentPost(
    imagesAssignedToCases: ClaimReferenceDocumentDto
  ): Observable<ClaimReferenceDocumentDto | null> {
    return this._recoveryClaimService
      .recoveryCasesCaseIdAttachmentsPost(
        imagesAssignedToCases.caseId ?? 0,
        this.apiVersion,
        { claimReferenceDocumentDto: imagesAssignedToCases }
      )
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  caseDocumentDelete(documentId: number): Observable<boolean> {
    return this._recoveryCaseDocumentService
      .caseDocumentsIdDelete(documentId, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return response.data;
        })
      );
  }

  inspectionsGet(
    _caseNumber: string,
    containerNumber: string | undefined,
    workOrderNumber: number | undefined,
    _incidentDate: Date | null,
    workOrderDate: Date | null,
    fromDateTime?: Date | null,
    toDateTime?: Date | null
  ): Observable<Array<Inspection> | null> {
    return this._recoveryInspectionService
      .inspectionsGet(
        containerNumber,
        fromDateTime,
        toDateTime,
        workOrderNumber && workOrderNumber > 500000000
          ? workOrderNumber
          : undefined,
        undefined,
        this.apiVersion
      )
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  saveImageAvailability(
    _inspectionInfo: InspectionDetailsDTO,
    _caseNumber: string
  ): Observable<boolean> {
    return of(true);
  }

  async saveAttachment(
    caseNumber: string,
    fromDateTime?: Date,
    toDateTime?: Date
  ): Promise<boolean> {
    let vendorData = await firstValueFrom(this.vendorRecoveryCaseData$);
    if (!vendorData) return false;
    vendorData.attachmentFromDate = fromDateTime;
    vendorData.attachmentToDate = toDateTime;
    const response = await firstValueFrom(
      this._recoveryClaimService
        .recoveryCasesCaseIdPut(vendorData.id ?? 0, vendorData)
        .pipe(
          map((response) => {
            if (!response || !response.data) return false;
            return true;
          }),
          catchError((_) => {
            return of(false);
          })
        )
    );
    return response;
  }
  async saveComment(comment: string): Promise<boolean> {
    let vendorData = await firstValueFrom(this.vendorRecoveryCaseData$);
    if (!vendorData) return false;
    vendorData.comment = comment;
    const response = await firstValueFrom(
      this._recoveryClaimService
        .recoveryCasesCaseIdPut(vendorData.id ?? 0, vendorData)
        .pipe(
          map((response) => {
            if (!response || !response.data) return false;
            return true;
          }),
          catchError((_) => {
            return of(false);
          })
        )
    );
    return response;
  }

  updateOverviewDetails(overview: TemplateModel[]) {
    this.overviewDetails$$.next(overview);
  }

  getActivityLogs(caseId: number): Observable<CaseActivityLogsDtoListResponse> {
    return this._recoveryClaimService.recoveryCasesCaseIdActivityLogsGet(
      caseId,
      RecoveryCaseType.Vendor,
      this.apiVersion
    );
  }

  reloadLiabilityPartyDetails() {
    this.reloadLiabilityPartyDetails$$.next(true);
  }

  reloadLiabilityLetters() {
    this.reloadLiabilityLetters$$.next(true);
  }

  reloadInvoices() {
    this.reloadInvoices$$.next(true);
  }

  updateIssueInvoiceVisibility(value: boolean) {
    this.shouldShowIssueInvoiceScreenSubject$$.next(value);
  }

  postInvoiceDetails(
    createInvoice: CreateInvoice,
    _userId: string
  ): Observable<boolean> {
    return this._invoiceDetailService
      .invoiceDetailsPost(createInvoice, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return response.data;
        })
      );
  }

  putInvoiceDetails(
    invoiceId: number,
    updateInvoice: UpdateInvoice,
    _userId: string
  ): Observable<boolean> {
    return this._invoiceDetailService
      .invoiceDetailsIdPut(invoiceId, updateInvoice, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return true;
        })
      );
  }

  updateCustomerData(data: CustomerInfoDto): void {
    this.customerDataSubject$$.next(data);
  }

  updateCaseCurrencyAndExchangeRate(
    currencyAndExchangeRate: CurrencyAndExchangeRateDto | undefined
  ): void {
    this.caseCurrencyAndExchangeRate$$.next(currencyAndExchangeRate);
  }

  reloadBulkInvoices(): void {
    return;
  }

  updateFactCode(_factCode: string | undefined): void {
    return;
  }
}
